/**
 * Ce qu'il faut retirer du planning atelier quand le travail se réduit.
 *
 * Corriger une intervention de 5 h à 3 h ne touche pas aux blocs déjà posés : ils
 * occupent un créneau de mécanicien à une heure dite, et le planning ne se réécrit pas
 * tout seul sous ses pieds. Mais l'atelier garde alors 2 h de travail fantôme, que rien
 * ne signale. Ce calcul dit exactement ce qu'il faudrait libérer, pour le proposer.
 *
 * On rogne par la FIN : raccourcir le dernier bloc ne peut chevaucher personne, là où
 * raccourcir le premier laisserait un trou au milieu de la journée. Un bloc entièrement
 * consommé est retiré.
 */

export interface PlacedJob {
  id: string;
  date: string;
  startHour: number;
  durationHours: number;
  workerName?: string;
}

export type JobAdjustment =
  | { id: string; job: PlacedJob; kind: "shorten"; durationHours: number }
  | { id: string; job: PlacedJob; kind: "remove" };

export interface ShrinkPlan {
  /** Heures déjà posées sur le planning. */
  placed: number;
  /** Heures que le travail demande encore. */
  needed: number;
  /** Heures posées en trop (0 si le planning est en dessous du besoin). */
  excess: number;
  adjustments: JobAdjustment[];
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export function planJobShrink(jobs: PlacedJob[], needed: number): ShrinkPlan {
  const placed = round2(jobs.reduce((sum, j) => sum + (j.durationHours || 0), 0));
  const excess = round2(placed - Math.max(needed, 0));
  if (excess <= 0 || jobs.length === 0) {
    return { placed, needed, excess: Math.max(excess, 0), adjustments: [] };
  }

  // Du plus tard au plus tôt : le dernier bloc de la série est celui qu'on peut
  // raccourcir sans rien déplacer.
  const chronological = [...jobs].sort((a, b) =>
    a.date === b.date ? a.startHour - b.startHour : a.date.localeCompare(b.date),
  );

  const adjustments: JobAdjustment[] = [];
  let left = excess;
  for (let i = chronological.length - 1; i >= 0 && left > 0; i--) {
    const job = chronological[i];
    const duration = job.durationHours || 0;
    if (left >= duration) {
      adjustments.push({ id: job.id, job, kind: "remove" });
      left = round2(left - duration);
    } else {
      adjustments.push({ id: job.id, job, kind: "shorten", durationHours: round2(duration - left) });
      left = 0;
    }
  }

  return { placed, needed, excess, adjustments };
}

/** "mar. 23/09 à 14h00 — Antoine", pour dire de quel bloc on parle. */
export function describeJob(job: PlacedJob): string {
  const when = job.date
    ? new Date(`${job.date}T12:00:00`).toLocaleDateString("fr-FR", {
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
      })
    : "—";
  const hours = Math.floor(job.startHour);
  const minutes = Math.round((job.startHour - hours) * 60);
  const at = `${hours}h${String(minutes).padStart(2, "0")}`;
  return job.workerName ? `${when} à ${at} — ${job.workerName}` : `${when} à ${at}`;
}
